import { normalizeBoard } from './BoardPack.mjs';
import { validateReplay } from './Replay.mjs';
import { downloadReplay } from './ReplayDownload.mjs';

const $ = id => document.getElementById(id);
const identity = board => JSON.stringify([board.id, board.name, board.layout]);

export const AnalysisUI = {
    init(workshop) {
        this.workshop = workshop;
        $('analyze-board').onclick = () => this.start();
        $('analysis-deep').onclick = () => this.start('deep');
        $('analysis-cancel').onclick = () => this.cancel('Validation canceled. No conclusion was reached.');
        $('analysis-watch').onclick = () => this.useRecording('watch');
        $('analysis-save').onclick = () => this.useRecording('save');
        $('analysis-recording-cancel').onclick = () => { this.cancelRecording('Recording preparation canceled. The successful result is still available.'); this.sync(); };
    },
    sync() {
        if (!this.workshop) return;
        if (this.snapshot && this.snapshot !== identity(this.workshop.current())) {
            this.finish();
            this.snapshot = null;
            this.resetRecording();
            $('analysis-status').textContent = 'Board changed. Validate again to check the current draft.';
            $('analysis-details').textContent = '';
            $('analysis-deep').hidden = true;
        }
        let valid = true;
        try { normalizeBoard(this.workshop.current()); } catch { valid = false; }
        $('analyze-board').disabled = !valid || !!this.worker || !!this.recordingTask;
        this.recordingControls();
        $('analyze-board').title = valid ? 'Check whether the solver can complete this draft' : 'Fix the board rules shown below before validating';
    },

    recordingControls() {
        $('analysis-recording-actions').hidden = !this.success;
        $('analysis-watch').disabled = $('analysis-save').disabled = !!this.recordingTask;
        $('analysis-recording-cancel').hidden = !this.recordingTask;
    },
    cancelRecording(message = '') {
        const task = this.recordingTask;
        if (!task) return;
        this.recordingTask = null;
        task.worker?.terminate();
        clearTimeout(task.deadline);
        task.resolve?.(null);
        $('analysis-recording-status').textContent = message;
        this.recordingControls();
    },
    resetRecording() {
        this.cancelRecording();
        this.success = null;
        $('analysis-recording-status').textContent = '';
        this.recordingControls();
    },
    prepareRecording(task) {
        return new Promise((resolve, reject) => {
            task.resolve = resolve;
            const worker = task.worker = new Worker('./solver-recording-worker.js');
            const settle = (recording, error) => {
                if (this.recordingTask !== task) return;
                worker.terminate();
                clearTimeout(task.deadline);
                if (error) reject(error); else resolve(recording);
            };
            worker.onmessage = ({ data }) => {
                if (data.type === 'recording') settle(data.recording);
                else settle(null, new Error(data.message || 'Could not create the recording.'));
            };
            worker.onerror = event => { event.preventDefault(); settle(null, new Error('Recording worker failed. Try again.')); };
            worker.onmessageerror = () => settle(null, new Error('Could not read the solver recording. Try again.'));
            task.deadline = setTimeout(() => settle(null, new Error('Recording preparation stopped after 60 seconds. Try again.')), 60000);
            worker.postMessage({ board: task.success.board, witness: task.success.witness });
        });
    },
    async useRecording(action) {
        this.sync();
        const success = this.success;
        if (!success || this.recordingTask) return;
        const task = this.recordingTask = { success };
        const current = () => this.recordingTask === task && this.success === success &&
            this.snapshot === identity(this.workshop.current());
        this.sync();
        $('analysis-recording-status').textContent = 'Preparing the first successful run…';
        try {
            if (!success.recording) {
                const recording = await this.prepareRecording(task);
                if (!current() || !recording) return;
                success.recording = validateReplay(recording);
            }
            if (!current()) return;
            if (action === 'watch') {
                // Navigating away cancels background jobs; this one is already finished.
                task.openingViewer = true;
                this.recordingTask = null;
                $('analysis-recording-status').textContent = 'First successful run · seed ' + success.witness.seed + '. Save recording to keep it after this visit.';
                success.itemId = this.workshop.replays.watchRecording(success.recording, success.itemId, success.saved);
            } else {
                const size = await downloadReplay(success.recording, current);
                if (!current() || size === null) return;
                success.saved = true;
                const item = this.workshop.replays.recordings.find(item => item.id === success.itemId);
                if (item) item.saved = true;
                $('analysis-recording-status').textContent = 'Recording download started (' + (size / 1024).toFixed(1) + ' KB). Open it in Recordings to watch later.';
            }
        } catch (error) {
            if (this.success === success && (this.recordingTask === task || (task.openingViewer && !this.recordingTask))) {
                $('analysis-recording-status').textContent = error.message;
            }
        } finally {
            task.worker?.terminate();
            clearTimeout(task.deadline);
            if (this.recordingTask === task) this.recordingTask = null;
            this.sync();
        }
    },
    finish() {
        this.worker?.terminate();
        this.worker = null;
        clearInterval(this.elapsedTimer);
        clearTimeout(this.deadline);
        $('analysis-cancel').hidden = true;
        $('board-analysis').setAttribute('aria-busy', 'false');
    },
    cancel(message) {
        this.cancelRecording('Recording preparation canceled when leaving Workshop. Try again to watch or save this run.');
        this.sync();
        if (!this.worker) return;
        this.finish();
        $('analysis-status').textContent = message;
        $('analysis-details').textContent = '';
        $('analysis-deep').hidden = true;
        this.sync();
    },
    start(mode = 'standard') {
        let board;
        try { board = normalizeBoard(this.workshop.current()); }
        catch (error) { this.workshop.notify(error.message); return; }
        this.finish();
        this.resetRecording();
        this.snapshot = identity(this.workshop.current());
        $('board-analysis').hidden = false;
        $('board-analysis').setAttribute('aria-busy', 'true');
        $('analysis-status').textContent = 'Searching routes and testing randomized runs…';
        $('analysis-details').textContent = (mode === 'deep' ? 'Deeper check: up to 250,000 search states and 100 runs.' : 'Standard check: up to 40,000 search states and 20 runs.') + ' Speed 3, first-pass difficulty. Runs locally in your browser.';
        $('analysis-deep').hidden = true;
        const started = Date.now();
        let worker;
        try {
            worker = new Worker('./analysis-worker.js');
            this.worker = worker;
            $('analysis-cancel').hidden = false;
            this.elapsedTimer = setInterval(() => {
                $('analysis-elapsed').textContent = Math.floor((Date.now() - started) / 1000) + 's elapsed';
            }, 1000);
            $('analysis-elapsed').textContent = '0s elapsed';
            const fail = message => {
                if (this.worker !== worker) return;
                this.finish();
                $('analysis-status').textContent = message;
                $('analysis-details').textContent = '';
                this.sync();
            };
            worker.onerror = event => {
                event.preventDefault();
                fail('Validation could not run. Reload the page and try again.');
            };
            worker.onmessageerror = () => fail('Validation could not return a result. Try again.');
            worker.onmessage = ({ data }) => {
                if (this.worker !== worker) return;
                if (data.type === 'error') { fail('Validation error: ' + data.message); return; }
                if (data.type !== 'result') { fail('Validation returned an unexpected result. Try again.'); return; }
                this.finish();
                $('analysis-elapsed').textContent = ((Date.now() - started) / 1000).toFixed(1) + 's';
                const report = data.report;
                const won = report.status === 'completion-demonstrated';
                if (won && report.witness) {
                    this.success = { board, witness: report.witness, saved: false };
                    $('analysis-recording-status').textContent = 'First successful run · seed ' + report.witness.seed + '. All trials finish after the first win. Save recording to keep it after this visit.';
                }
                $('analysis-status').textContent = won ? 'Completion demonstrated' :
                    report.status === 'no-safe-route-in-relaxed-model' ? 'No route found in the planning model' : 'Inconclusive — no completion demonstrated';
                const route = report.routeMetrics ? 'Route: ' + report.routeMetrics.moves + ' moves. ' : '';
                const trials = report.replay.tested ? report.replay.wins + ' of ' + report.replay.tested + ' randomized runs completed. ' : 'No route was available for randomized runs. ';
                const budget = report.route.status === 'budget-exhausted' ? 'The search limit was reached. ' : '';
                $('analysis-details').textContent = route + trials + budget +
                    'Searched ' + report.route.searchNodes.toLocaleString() + ' states. Speed 3, first-pass difficulty. ' +
                    (won ? 'A successful run uses real physics and hazards; this is not a shortest-path or difficulty rating.' : 'This does not prove the board impossible; other routes or random outcomes may succeed.');
                $('analysis-deep').hidden = won || mode === 'deep';
                this.sync();
            };
            this.deadline = setTimeout(() => {
                if (this.worker !== worker) return;
                fail('Inconclusive — validation stopped after 60 seconds. No conclusion about solvability.');
            }, 60000);
            worker.postMessage({ board, mode });
        } catch {
            this.finish();
            $('analysis-status').textContent = 'Validation could not start in this browser. Reload the page or try another browser.';
            $('analysis-details').textContent = '';
        }
        this.sync();
    }
};
